import type { AnalyticsEventParamsOf } from "@v2/features/infra/analytics/events";
import type { LucideIcon } from "lucide-react";
import type { User } from "schemas/generated/user";

import * as React from "react";

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@replohq/lattice/components/sidebar/Sidebar";
import { cn } from "@replohq/lattice/utils/cn";
import {
  useCurrentProjectContext,
  useIsAgentEnabled,
} from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import LearnMoreCollapsibleNavItem from "@v2/features/dashboard/sidebar/dashboard-sidebar-nav/LearnMoreCollapsibleNavItem";
import { DesignLibraryIcon } from "@v2/features/design-library/designLibraryIcon";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { routes } from "@v2/utils/router";
import {
  Bot,
  ChartLine,
  FlaskConical,
  GalleryVerticalEnd,
  Home,
  MessageSquareMore,
  PackageOpen,
  Settings,
} from "lucide-react";
import { generatePath, Link, useLocation } from "react-router-dom";
import { filterNulls } from "replo-utils/lib/array";

type NavItem = {
  title: string;
  icon: LucideIcon;
  path: string;
  href: string;
  analyticsTab: AnalyticsEventParamsOf<"dashboard.nav.click">["tab"];
};

/**
 * Builds nav item groups based on the current project type.
 * Each group is separated by a divider in the sidebar.
 */
function buildNavItemGroups({
  project,
  workspaceId,
  isAgentEnabled,
}: {
  project: ReturnType<typeof useCurrentProjectContext>["project"];
  workspaceId: string | null;
  isAgentEnabled: boolean;
}): NavItem[][] {
  if (!project) {
    if (workspaceId) {
      return [
        [
          {
            title: "Settings",
            icon: Settings,
            path: `/workspace/${workspaceId}/settings`,
            href: `/workspace/${workspaceId}/settings`,
            analyticsTab: "settings",
          },
        ],
      ];
    }
    return [];
  }

  const isReplo = project.hostingType === "replo";
  const baseRoute = isAgentEnabled ? "agent" : "shops";
  const experimentListPath =
    workspaceId &&
    generatePath(routes.shops.experiments.list, {
      projectId: project.id,
    });

  const groups: NavItem[][] = [];

  if (isReplo) {
    // Group 1: Home + Chat (legacy) or Agent (new)
    if (isAgentEnabled) {
      groups.push([
        {
          title: "Agent",
          icon: Bot,
          path: `/${baseRoute}/${project.id}`,
          href: `/${baseRoute}/${project.id}`,
          analyticsTab: "chat",
        },
      ]);
    } else {
      groups.push([
        {
          title: "Home",
          icon: Home,
          path: `/${baseRoute}/${project.id}/home`,
          href: `/${baseRoute}/${project.id}/home`,
          analyticsTab: "home",
        },
        {
          title: "Chat",
          icon: MessageSquareMore,
          path: `/${baseRoute}/${project.id}/chat`,
          href: `/${baseRoute}/${project.id}/chat`,
          analyticsTab: "chat",
        },
      ]);
    }

    // Group 2: Analytics, A/B Testing
    const analyticsGroup: NavItem[] = [
      {
        title: "Analytics",
        icon: ChartLine,
        path: `/shops/${project.id}/analytics`,
        href: `/shops/${project.id}/analytics/home`,
        analyticsTab: "analytics_overview",
      },
    ];
    if (experimentListPath) {
      analyticsGroup.push({
        title: "A/B Testing",
        icon: FlaskConical,
        path: experimentListPath,
        href: experimentListPath,
        analyticsTab: "experiments",
      });
    }
    groups.push(analyticsGroup);

    // Group 3: Design Library, Products, Settings
    groups.push(
      filterNulls([
        !isAgentEnabled
          ? {
              title: "Design Library",
              icon: DesignLibraryIcon,
              path: `/shops/${project.id}/design-library`,
              href: `/shops/${project.id}/design-library`,
              analyticsTab: "design_library",
            }
          : null,
        {
          title: "Products",
          icon: PackageOpen,
          path: `/shops/${project.id}/products`,
          href: `/shops/${project.id}/products`,
          analyticsTab: "products",
        },
      ]),
    );

    // group 4: settings
    groups.push([
      {
        title: "Settings",
        icon: Settings,
        path: `/shops/${project.id}/settings`,
        href: `/shops/${project.id}/settings`,
        analyticsTab: "settings",
      },
    ]);
  } else {
    // Shopify project: single group
    const items: NavItem[] = [
      {
        title: "Pages",
        icon: GalleryVerticalEnd,
        path: `/shops/${project.id}/site`,
        href: `/shops/${project.id}/site`,
        analyticsTab: "shop_site",
      },
      {
        title: "Analytics",
        icon: ChartLine,
        path: `/shops/${project.id}/analytics`,
        href: `/shops/${project.id}/analytics/home`,
        analyticsTab: "analytics_overview",
      },
    ];
    if (experimentListPath) {
      items.push({
        title: "A/B Testing",
        icon: FlaskConical,
        path: experimentListPath,
        href: experimentListPath,
        analyticsTab: "experiments",
      });
    }
    items.push({
      title: "Settings",
      icon: Settings,
      path: `/shops/${project.id}/settings`,
      href: `/shops/${project.id}/settings`,
      analyticsTab: "settings",
    });
    groups.push(items);
  }

  return groups;
}

const DashboardSidebarNavItem = ({
  item,
  workspaceId,
  user,
}: {
  item: NavItem;
  workspaceId: string | null;
  user: User | null;
}) => {
  const { open } = useSidebar();
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.path);
  const logEvent = useLogAnalytics();

  return (
    <SidebarMenuItem key={item.title}>
      <SidebarMenuButton
        render={
          <Link
            to={item.href}
            onClick={() => {
              logEvent("dashboard.nav.click", {
                tab: item.analyticsTab,
                userId: user?.id ?? null,
                workspaceId: workspaceId ?? null,
              });
            }}
          />
        }
        tooltip={item.title}
        isActive={isActive}
        UNSAFE_className={cn(isActive && !open && "bg-default!")}
      >
        <item.icon className={cn(isActive && !open && "text-white")} />
        <span>{item.title}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export default function DashboardSidebarNav() {
  const { project } = useCurrentProjectContext();
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId();
  const isAgentEnabled = useIsAgentEnabled();
  const navItems = buildNavItemGroups({
    project,
    workspaceId: workspaceId ?? null,
    isAgentEnabled,
  });

  return (
    <SidebarGroup UNSAFE_className="pt-0">
      <SidebarMenu>
        {navItems.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && <SidebarSeparator />}
            {group.map((item) => (
              <DashboardSidebarNavItem
                key={item.title}
                item={item}
                workspaceId={workspaceId ?? null}
                user={user ?? null}
              />
            ))}
          </React.Fragment>
        ))}
        <LearnMoreCollapsibleNavItem />
      </SidebarMenu>
    </SidebarGroup>
  );
}
